import type { MedBasis, MissingDataStream } from "./complianceFilters";

// "data_unavailable": a check's PRO data couldn't be loaded, so the patient isn't known to be
// compliant.
export type Triage =
  | "out_of_compliance"
  | "at_risk"
  | "data_unavailable"
  | "steady"
  | "unscoreable"
  | "dnf"
  | "done";

// Outreach state; "exhausted" sends an ended window to Do not follow up.
export type ContactState =
  | "none"
  | "fresh"
  | "contacted"
  | "due"
  | "exhausted"
  | "unknown";

// POST /v3/compliance/pro, as the API returns it. Timestamps are UTC; null means nothing in
// the last 90 days.
export interface ProMedicationDto {
  item_id: number;
  rune_classification: string;
  pd_tag: string | null; // eg "medication.pd.levodopa"; null for non-PD medicines
  scheduled: boolean; // has an active medication schedule
  // Any non-autolog dose the patient logged, including skipped and failed ones: each accounts for the dose.
  last_logged_at: string | null;
  last_autologged_at: string | null;
}

export interface ProPatientDto {
  patient_id: string;
  timezone: string; // IANA; "UTC" when timezone_missing
  timezone_missing: boolean;
  medications: ProMedicationDto[];
  bowel_movement: { last_logged_at: string | null };
}

export interface PatientMedication {
  itemId: number;
  runeClassification: string;
  isPd: boolean; // has a PD tag
  scheduled: boolean;
  lastLoggedAt: string | null;
  lastAutologgedAt: string | null;
}

export interface DailyStreamDto {
  date: string; // YYYY-MM-DD, patient-local
  present: boolean;
  value: number | null; // MM4PD hours; null for other streams
}

export interface DailyMedicationDto {
  date: string; // YYYY-MM-DD, patient-local
  item_id: number;
  rune_classification: string;
  pd_tag: string | null;
  scheduled: number;
  taken: number;
  on_time: number;
  skipped: number;
  scheduled_autolog: number;
  autolog_logged: number;
  last_taken_at: string | null; // UTC timestamp
  last_autolog_at: string | null;
}

// Why a stream or section is missing from the daily summary.
export type DailySummaryErrorCode =
  | "upstream_timeout"
  | "upstream_error"
  | "multiple_streams"; // the patient has more than one matching stream

export interface DailySummaryDto {
  patient_id: string;
  timezone: string; // IANA; "UTC" when timezone_missing
  timezone_missing: boolean;
  start_date: string; // YYYY-MM-DD, patient-local
  end_date: string;
  streams: {
    mm4pd: DailyStreamDto[];
    steps: DailyStreamDto[];
    sleep: DailyStreamDto[];
    tap_test: DailyStreamDto[];
  };
  medication: {
    days: DailyMedicationDto[];
  };
  bowel_movements: Array<{ date: string; present: boolean }>;
  errors: Record<string, DailySummaryErrorCode>; // keyed by stream or section name
}

// CarrotGraph complianceOutreachList
export interface ComplianceOutreachDto {
  id: string;
  messageId: string;
  contactedAt: number; // Unix seconds
}

// Derived per medication basis from the patient's medicines. Recency only for now; adherence
// will be added to the patient list later.
export interface MedRecent {
  basis: MedBasis;
  // False flags the patient: no schedule usually means it wasn't set up.
  hasSchedule: boolean;
  lastLoggedAt: string | null;
  // Patient-local days; null if no dose in the last 90 days.
  daysSinceLastData: number | null;
  // Annotation only: the app records autologs without the patient doing anything, so they
  // never count towards compliance.
  autologOn: boolean;
}

export interface BowelMovementRecent {
  // Patient-local days; null if no bowel movement logged in the last 90 days.
  daysSinceLastData: number | null;
}

export interface Contact {
  state: ContactState;
  gapDays: number | null;
  attempts: number;
}

export interface ComplianceOrg {
  id: string;
  displayName: string;
  category: "pharma" | "health_system" | "other";
  totalPatients: number;
  projects: ComplianceProject[];
}

export interface ComplianceProject {
  id: string;
  displayName: string;
  orgId: string;
  patientCount: number;
  // Loaded with the org list, so opening a project doesn't fetch every config again.
  config: ProjectComplianceConfig;
}

// One project at a time, so a single compliance configuration applies.
export interface ComplianceQueryParams {
  orgId: string;
  projectId: string;
  projectConfig: ProjectComplianceConfig;
  // From the org list. Null if they failed to load: every patient is then checked as if they
  // had no exemption.
  exemptions: PatientExemptions | null;
}

// A PRO batch that failed; its patients are listed without medication or bowel movements.
export type ComplianceProFailed = (
  patientIds: string[],
  error: unknown
) => void;

export interface ComplianceOrgList {
  orgs: ComplianceOrg[];
  // Every patient's exemption, for all projects; null if the list failed to load.
  exemptions: PatientExemptions | null;
}

export interface ComplianceOrgCallbacks {
  onSkipped?: ComplianceSkippedRead;
  onExemptionsFailed?: (error: unknown) => void;
}

// A project or org read the org list skipped because it failed.
export type ComplianceSkippedRead = (
  resource: "ComplianceProject" | "ComplianceOrg",
  id: string,
  error: unknown
) => void;

export interface StreamCoverage {
  name: string;
  // Patient-local days since the device last recorded data.
  daysSinceLastData: number | null;
  exempt: boolean;
  exemptWhy: string | null;
}

export interface PatientExemption {
  streams: string[];
  reason: string | null;
}

// Keyed by patient ID.
export type PatientExemptions = Map<string, PatientExemption>;

export interface PatientRow {
  patientId: string;
  projectId: string;
  codeName: string;
  codeNameMissing: boolean;
  triage: Triage;
  // One line each; empty when there's nothing to explain.
  triageReasons: string[];
  // Failed (or at-risk) checks; orders rows within a group.
  flaggedChecks: number;
  // Breaks ties in row order.
  longestSilenceDays: number | null;
  // Kept so `medRecent` can be re-derived when the medication basis changes.
  medications: PatientMedication[];
  // False if the patient's PRO batch failed. The medication and bowel movement checks are then
  // skipped rather than failed as "no schedule", and `timezone` is a UTC placeholder rather
  // than a known-missing zone.
  proLoaded: boolean;
  medRecent: MedRecent;
  bowelMovementRecent: BowelMovementRecent;
  streams: StreamCoverage[];
  // Strive time zone (IANA): one clock for every recency count.
  timezone: string;
  timezoneMissing: boolean;
  contact: Contact;
  exemption: PatientExemption | null;
  window: {
    start: string;
    end: string;
    isActive: boolean;
  };
  appMode: string | null;
  createdAt: string;
  lastOutreachDate: string | null;
}

// Mirrors carrotgraph's ProjectComplianceConfig (StrivePD › Admin › Compliance ›
// Configurations), the source of truth for a project's checks; StriveStudy only overrides it
// for the session.
export interface ProjectComplianceConfig {
  projectId: string;
  categories: MissingDataStream[];
  // Days without data before a check fails, for every stream including medication.
  complianceThresholdDays: number;
  // Days without data before a check is at risk; at most complianceThresholdDays.
  atRiskThresholdDays: number;
  outreachGapDays: number; // contact is due after this many days without outreach
  maxOutreachAttempts: number | null; // outreach stops after this many attempts
  // Rune classifications, as stored in study_medications; names vary too much to match on.
  studyMedications: string[];
}

export interface ComplianceDataset {
  windowStart: string;
  windowEnd: string;
  patients: PatientRow[];
  // Patients whose PRO batch failed (proLoaded false).
  proFailedCount: number;
}

// Parsed sidebar data
export interface DailyStreamDay {
  date: string;
  present: boolean;
  value: number | null;
}

export interface DailyMedicationRow {
  date: string;
  itemId: number;
  runeClassification: string;
  pdTag: string | null;
  scheduled: number;
  taken: number;
  onTime: number;
  skipped: number;
  scheduledAutolog: number;
  autologLogged: number;
  lastTakenAt: string | null;
  lastAutologAt: string | null;
}

export type DayStatus =
  | "in_progress"
  | "no_schedule"
  | "autolog_only"
  | "scheduled";

export interface DailyMedDay {
  date: string;
  scheduled: number;
  taken: number;
  skipped: number;
  // Taken or skipped, capped at scheduled.
  // While monitoring compliance for an active study,
  // Ops primarily cares that doses are accounted for
  // regardless of whether the doses were taken or skipped.
  accounted: number;
  scheduledAutolog: number;
  autologLogged: number;
  status: DayStatus;
}

export type OutreachSource = "intercom" | "zendesk";

export interface OutreachEvent {
  source: OutreachSource;
  occurredAt: string; // ISO 8601
  channel: string;
  externalId: string;
}

export interface PatientSidebar {
  timezone: string;
  timezoneMissing: boolean;
  startDate: string;
  endDate: string;
  streams: Partial<Record<MissingDataStream, DailyStreamDay[]>>;
  medicationDays: DailyMedicationRow[];
  errors: Partial<Record<MissingDataStream, DailySummaryErrorCode>>;
  outreach: OutreachEvent[];
}

export const TRIAGE_ORDER: Triage[] = [
  "out_of_compliance",
  "at_risk",
  "data_unavailable",
  "steady",
  "unscoreable",
  "dnf",
  "done"
];

export const CONTACT_LABELS: Record<ContactState, string> = {
  none: "never",
  fresh: "recent",
  contacted: "contacted",
  due: "due",
  exhausted: "stopped",
  unknown: "—"
};

export const STREAM_DISPLAY: Record<string, { short: string; long: string }> = {
  tremor_dyskinesia: { short: "T&D", long: "Tremor & dys." },
  mobility: { short: "mobility", long: "Mobility" },
  sleep: { short: "sleep", long: "Sleep" },
  medication: { short: "medication", long: "Medication" },
  tap_test: { short: "tap test", long: "Tap test" },
  bowel_movement: { short: "BM", long: "Bowel movement" }
};

export const STREAM_ORDER: string[] = [
  "medication",
  "tremor_dyskinesia",
  "mobility",
  "sleep",
  "tap_test",
  "bowel_movement"
];

export const TRIAGE_LABELS: Record<Triage, string> = {
  out_of_compliance: "Out of compliance",
  at_risk: "At risk",
  data_unavailable: "Could not retrieve data",
  steady: "Steady",
  unscoreable: "Recently added",
  dnf: "Do not follow up",
  done: "Completed"
};
